import generated from "./tokenDecimals.generated.json";

/**
 * Authoritative token decimals captured by an explicit one-time
 * `yarn verify-token-decimals` refresh and checked into the bundle. Normal
 * builds do not hit RPC. Keyed by lowercased token address.
 */
const ONCHAIN_TOKEN_DECIMALS: Record<string, number> =
  (generated as { decimals?: Record<string, number> }).decimals || {};

/**
 * Returns the real on-chain decimals for a token address, or `undefined` when
 * the address is not a known supported token on the active chain. Callers must
 * NOT substitute a fallback — an unknown token's amount cannot be displayed
 * reliably (e.g. a 6-decimal amount scaled by 18 renders as "0.00").
 */
export function getOnchainTokenDecimals(address?: string): number | undefined {
  if (!address) return undefined;
  return ONCHAIN_TOKEN_DECIMALS[address.toLowerCase()];
}

/**
 * Chain id the `tokenDecimals.generated.json` file was most recently verified
 * against (its `lastVerifiedChainId`). The override below is trusted ONLY for
 * this chain so a stale cross-chain entry can never win over a correct config.
 */
const VERIFIED_CHAIN_ID: number | undefined = (() => {
  const v = (generated as { lastVerifiedChainId?: string }).lastVerifiedChainId;
  return v ? Number(v) : undefined;
})();

const driftWarned = new Set<number>();

/**
 * Resolve the USDC decimals to use, preferring the authoritative on-chain
 * verified value (from `tokenDecimals.generated.json`, refreshed by
 * `yarn verify-token-decimals`) over the static `configDecimals` — but ONLY for
 * the chain that file was last verified against and ONLY when an entry exists
 * for `usdcAddress`. This guards against static-config drift (e.g. configs.ts
 * being hand-edited to a wrong value, as in commit d51febe which set Monad USDC
 * to 18 when the token is 6 on-chain). When nothing trustworthy is available it
 * returns `configDecimals` unchanged, so non-verified chains keep their config.
 */
export function resolveUsdcDecimals(
  chainId: number | undefined,
  usdcAddress: string | undefined,
  configDecimals: number
): number {
  if (chainId === undefined || chainId !== VERIFIED_CHAIN_ID) return configDecimals;
  const verified = getOnchainTokenDecimals(usdcAddress);
  if (verified === undefined || verified === configDecimals) return configDecimals;
  if (!driftWarned.has(chainId)) {
    driftWarned.add(chainId);
    // eslint-disable-next-line no-console
    console.warn(
      `[tokenDecimals] USDC decimals drift on chain ${chainId}: static config says ` +
        `${configDecimals} but on-chain (verified) is ${verified} for ${usdcAddress}. ` +
        `Using ${verified}. Update src/config/chains/configs.ts to match ` +
        `(re-run \`yarn verify-token-decimals\`).`
    );
  }
  return verified;
}
